import { z } from "zod/v4";

export const videoAspectRatios = [
  "16:9",
  "9:16",
  "1:1",
] as const satisfies readonly string[];

export type VideoAspectRatio = (typeof videoAspectRatios)[number];

export const genders = [
  "male",
  "female",
  "non-binary",
  "prefer-not-to-say",
  "other",
  "unknown",
] as const satisfies readonly string[];

export type Gender = (typeof genders)[number];

export const streamingServices = [
  "twitch",
  "tiktok",
  "instagram",
  "youtube",
  "twitter",
  "other",
] as const satisfies readonly string[];

export type StreamingService = (typeof streamingServices)[number];

export const platformTypes = [
  "instagram_reels",
  "instagram_stories",
  "instagram_feed",
  "instagram_demographics",
  "instagram_account",
  "tiktok",
  "youtube",
] as const satisfies readonly string[];

export type PlatformType = (typeof platformTypes)[number];

export const socialServices = [
  "tiktok",
  "facebook",
  "youtube",
  "instagram",
  "twitter",
  "snapchat",
  "cameo",
  "twitch",
] as const satisfies readonly string[];

export type SocialService = (typeof socialServices)[number];

export type BrandDirectorySocial = {
  service: SocialService;
  username: string;
};

export const brandDirectoryStatuses = [
  "active",
  "archived",
] as const satisfies readonly string[];

export type BrandDirectoryStatus = (typeof brandDirectoryStatuses)[number];

export const brandWatchlistStatuses = [
  "draft",
  "active",
  "paused",
  "archived",
] as const satisfies readonly string[];

export type BrandWatchlistStatus = (typeof brandWatchlistStatuses)[number];

export const marketingPageTypes = [
  "article",
  "legal",
] as const satisfies readonly string[];

export type MarketingPageType = (typeof marketingPageTypes)[number];

export const brandScrapeJobStatuses = [
  "pending",
  "running",
  "completed",
  "failed",
] as const satisfies readonly string[];

export type BrandScrapeJobStatus = (typeof brandScrapeJobStatuses)[number];

export const competitorAdAnalysisStatuses = [
  "pending",
  "completed",
  "failed",
] as const satisfies readonly string[];

export type CompetitorAdAnalysisStatus =
  (typeof competitorAdAnalysisStatuses)[number];

export const pitchStatuses = [
  "submitted",
  "qa_approved",
  "qa_rejected",
] as const satisfies readonly string[];

export type PitchStatus = (typeof pitchStatuses)[number];

export const videoStorageProviders = [
  "aws",
  "firebase",
  "custom",
] as const satisfies readonly string[];

export type VideoStorageProvider = (typeof videoStorageProviders)[number];

export const bestWorkPlatforms = [
  "instagram",
  "tiktok",
] as const satisfies readonly string[];

export type BestWorkPlatform = (typeof bestWorkPlatforms)[number];

const excludedFromLinkInBio = [
  "facebook",
  "snapchat",
  "twitter",
] as const satisfies readonly SocialService[];
type ExcludedFromLinkInBio = (typeof excludedFromLinkInBio)[number];

export const linkInBioSocialServices = [
  "eventbrite",
  "other",
  ...socialServices.filter(
    (service): service is Exclude<SocialService, ExcludedFromLinkInBio> =>
      !(excludedFromLinkInBio as readonly SocialService[]).includes(service),
  ),
] as const satisfies readonly string[];

export type LinkInBioSocialService = (typeof linkInBioSocialServices)[number];

export const linkInBioSocialServicesWithLinkInput = [
  "youtube",
] as const satisfies readonly LinkInBioSocialService[];

export const creatorTypes = [
  "individual",
  "group",
] as const satisfies readonly string[];

export type CreatorType = (typeof creatorTypes)[number];

export const orderStatuses = [
  "waiting_for_upload",
  "uploaded",
  "completed",
  "cancelled",
  "stale",
] as const satisfies readonly string[];

export type OrderStatus = (typeof orderStatuses)[number];

export const videoFormats = [
  "tiktok",
  "instagram_reel",
  "instagram_stories",
  "snapchat",
  "livestream",
  "youtube",
  "youtube_shorts",
  "other_deliverable",
] as const satisfies readonly string[];

export type VideoFormat = (typeof videoFormats)[number];

export const transferStatuses = [
  "pending",
  "completed",
  "failed",
  "stopped",
] as const satisfies readonly string[];

export type TransferStatus = (typeof transferStatuses)[number];

export const invoiceStatuses = [
  "draft",
  "submitted",
  "deleted",
  "authorised",
  "paid",
  "voided",
] as const satisfies readonly string[];

export type InvoiceStatus = (typeof invoiceStatuses)[number];

export type CreatorGuideItem = {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  targetUrl: string;
};

export const CREATOR_TOOL_PAGE_CONFIG_KEY = "creator-app-tools-page-config";
export const AI_CHAT_PERSONALITY_KEY = "ai-chat-personality";
export const AI_SCRAPE_GENERATE_BIO_KEY = "ai-scrape-generate-bio";
export const AI_SCRAPE_GENERATE_CATEGORIES_KEY =
  "ai-scrape-generate-categories";
export const AI_SCRAPE_GENERATE_KEYWORDS_KEY = "ai-scrape-generate-keywords";
export const AI_SCRAPE_POLITICAL_TOPICS_KEY = "ai-scrape-political-topics";
export const AI_ADMIN_VAT_EXTRACTOR_KEY = "ai-admin-vat-extractor";
export const AI_MISSION_IDEAS_GENERATOR_KEY = "ai-mission-ideas-generator";
export const AI_CREATOR_RECOMMENDATIONS_GENERATOR_KEY =
  "ai-creator-recommendations-generator";
export const MARKETING_APP_PRIVACY_POLICY_KEY = "marketing-app-privacy-policy";
export const MARKETING_APP_TERMS_KEY = "marketing-app-terms";
export const AI_MARKETING_CREATOR_AUDIENCE_SUMMARY_KEY =
  "ai-marketing-creator-audience-summary";
export const AI_MARKETING_CREATOR_BRANDS_SUMMARY_KEY =
  "ai-marketing-creator-brands-summary";
export const AI_MARKETING_CREATOR_SIMILAR_CREATORS_INTRO_KEY =
  "ai-marketing-creator-similar-creators-intro";
export const AI_MARKETING_CREATOR_CONTENT_STYLE_KEY =
  "ai-marketing-creator-content-style";
export const AI_MARKETING_PLAYLIST_GENERATOR_KEY =
  "ai-marketing-playlist-generator";
export const AI_COMPETITOR_ANALYSIS_AGENT_KEY = "competitor-analysis-agent";
export const AI_DEAL_MEMO_SUMMARY_PROMPT_KEY = "ai-deal-memo-summary-prompt";

export const adminConfigurationKeys = [
  CREATOR_TOOL_PAGE_CONFIG_KEY,
  AI_CHAT_PERSONALITY_KEY,
  AI_SCRAPE_GENERATE_BIO_KEY,
  AI_SCRAPE_GENERATE_CATEGORIES_KEY,
  AI_SCRAPE_GENERATE_KEYWORDS_KEY,
  AI_SCRAPE_POLITICAL_TOPICS_KEY,
  AI_ADMIN_VAT_EXTRACTOR_KEY,
  AI_MISSION_IDEAS_GENERATOR_KEY,
  AI_CREATOR_RECOMMENDATIONS_GENERATOR_KEY,
  MARKETING_APP_PRIVACY_POLICY_KEY,
  MARKETING_APP_TERMS_KEY,
  AI_MARKETING_CREATOR_AUDIENCE_SUMMARY_KEY,
  AI_MARKETING_CREATOR_BRANDS_SUMMARY_KEY,
  AI_MARKETING_CREATOR_SIMILAR_CREATORS_INTRO_KEY,
  AI_MARKETING_CREATOR_CONTENT_STYLE_KEY,
  AI_MARKETING_PLAYLIST_GENERATOR_KEY,
  AI_COMPETITOR_ANALYSIS_AGENT_KEY,
  AI_DEAL_MEMO_SUMMARY_PROMPT_KEY,
] as const satisfies readonly string[];

export type AdminConfigurationKeys = (typeof adminConfigurationKeys)[number];

export const dealMemoStatuses = [
  "shortlist.suggested",
  "shortlist.rejected",
  "outreach.pending",
  "outreach.message_sent",
  "outreach.reachout_to_creator",
  "outreach.no_response",
  "responded.priced",
  "responded.mission_edits",
  "responded.declined_by_creator",
  "responded.rejected_by_brand",
  "selected.review_deal",
  "contract.creating",
  "contract.draft",
  "contract.failed",
  "contract.pending",
  "contract.sent",
  "contract.viewed",
  "contract.suggested_edits",
  "contract.signed",
  "contract.deal_complete",
] as const satisfies readonly string[];

export type DealMemoStatus = (typeof dealMemoStatuses)[number];

export const missionStatuses = [
  "created",
  "shortlisting_creators",
  "outreach",
  "negotiations",
  "contracting",
  "in_production",
  "delivering",
  "complete",
  "cancelled",
] as const satisfies readonly string[];

export type MissionStatus = (typeof missionStatuses)[number];

/**
 * Mission statuses that represent an active (non-terminal) mission.
 * Use this to filter missions that are currently in-progress.
 */
export const ACTIVE_MISSION_STATUSES = [
  "created",
  "shortlisting_creators",
  "outreach",
  "negotiations",
  "contracting",
  "in_production",
  "delivering",
] as const satisfies MissionStatus[];

export const missionTypes = [
  "paid",
  "gifting",
] as const satisfies readonly string[];

export type MissionType = (typeof missionTypes)[number];

export const dealMemoDeliverableStatuses = [
  "in_production",
  "submission_received",
  "waiting_for_creator_to_post",
  "creator_posted",
  "approved",
  "complete",
] as const satisfies readonly string[];

export type DealMemoDeliverableStatus =
  (typeof dealMemoDeliverableStatuses)[number];

export const fixedMonthTimeOptions = [
  "1 month",
  "2 months",
  "3 months",
  "4 months",
  "5 months",
  "6 months",
  "7 months",
  "8 months",
  "9 months",
  "10 months",
  "11 months",
  "12 months",
  "In perpetuity",
];
export type FixedMonthTimeOption = (typeof fixedMonthTimeOptions)[number];

export const videoSubmissionStatus = [
  "new",
  "rejected",
  "feedback_sent",
  "deleted",
  "approved",
  "waiting_to_post",
  "live",
] as const satisfies readonly string[];

export type VideoSubmissionStatus = (typeof videoSubmissionStatus)[number];

export const videoAdditionSources = [
  "scraped",
  "platform_upload_page",
  "connected_post",
  "external_deliverable_link",
] as const satisfies readonly string[];

export type VideoAdditionSource = (typeof videoAdditionSources)[number];

export const videoSource = [
  "upload",
  "tiktok",
  "instagram",
] as const satisfies readonly string[];

export type VideoSource = (typeof videoSource)[number];

export const creatorTalentAssignmentTypes = [
  "self-represented",
  "managed",
] as const satisfies readonly string[];

export type CreatorTalentAssignmentType =
  (typeof creatorTalentAssignmentTypes)[number];

export const organizationTypes = [
  "brand",
  "agency",
] as const satisfies readonly string[];

export type OrganizationTypes = (typeof organizationTypes)[number];

export const serviceLevels = [
  "self_service",
  "managed_service_with_signatory",
  "managed_service_without_signatory",
] as const satisfies readonly string[];

export type ServiceLevel = (typeof serviceLevels)[number];

export const dealMemoSignerTypes = [
  "creator",
  "talent_agent",
  "organisation",
  "platform",
] as const satisfies readonly string[];

export type DealMemoSignerStatus = (typeof dealMemoSignerTypes)[number];

export const rejectionReasons = [
  "Preferred a different creator",
  "Quote was out of budget",
  "Quote seemed too high for this creator",
  "Not available when needed",
  "Has exclusivity or has worked with a competitor",
  "Poor communication",
  "Other",
] as const;

export type RejectionReasonOptions = (typeof rejectionReasons)[number];

export type RejectionReason = {
  reasons: RejectionReasonOptions[];
  comment?: string;
};

export const creatorOnboardingSteps = [
  "welcome",
  "whatsapp_phone_number",
  "whatsapp_verification",
  "enter_details",
  "connect_socials",
  "enter_postal_address",
  "representation",
  "agree_terms",
  "connect_stripe",
  "complete_onboarding",
] as const satisfies readonly string[];
export type CreatorOnboardingStep = (typeof creatorOnboardingSteps)[number];

export const creatorOnboardingUtmSchema = z.object({
  utmSource: z.string().min(1, { error: "UTM Source is required" }),
  utmCampaign: z.string().min(1, { error: "UTM Campaign is required" }),
  utmMedium: z.string().min(1, { error: "UTM Medium is required" }),
  utmContent: z.string().optional(),
  utmTerm: z.string().optional(),
  inviteChannel: z.string().optional(),
  invitedAt: z.string().optional(),
  stalledInviteAlertSentAt: z.string().optional(),
});

export type CreatorOnboardingUtm = z.infer<typeof creatorOnboardingUtmSchema>;

export const socialScrapeStepState = [
  "not_started",
  "in_progress",
  "completed",
  "error",
] as const satisfies readonly string[];

export type SocialScrapeStepState = (typeof socialScrapeStepState)[number];

export const socialScrapeStep = [
  "scraping_profile",
  "transcribing_videos",
  "generating_bio",
  "categorizing_creator",
  "generating_keywords",
  "calculating_video_metrics",
  "counting_swear_jar",
  "finding_controversial_topics",
] as const satisfies readonly string[];

export type SocialScrapeStep = (typeof socialScrapeStep)[number];

export const scrapePlatforms = [
  "instagram",
  "tiktok",
] as const satisfies readonly string[];

export type ScrapePlatform = (typeof scrapePlatforms)[number];

export const creatorContactFormats = [
  "whatsapp",
  "email",
] as const satisfies readonly string[];

export type CreatorContactFormat = (typeof creatorContactFormats)[number];

export const whatsappBlastStatuses = [
  "draft",
  "sent",
  "failed",
] as const satisfies readonly string[];

export type WhatsappBlastStatus = (typeof whatsappBlastStatuses)[number];

export const whatsappMessageLogStatuses = [
  "queued",
  "failed",
  "sent",
  "delivered",
  "read",
  "clicked",
] as const satisfies readonly string[];

export type WhatsappMessageLogStatus =
  (typeof whatsappMessageLogStatuses)[number];

// The chat-based tools a conversation can belong to. MySQL enums are
// order-sensitive (do not reorder); append new values only.
export const agentConversationTools = [
  "intelligence",
] as const satisfies readonly string[];

export type AgentConversationTool = (typeof agentConversationTools)[number];

// Message roles persisted to a MySQL enum. Order-sensitive; append-only.
export const agentConversationMessageRoles = [
  "user",
  "assistant",
] as const satisfies readonly string[];

export type AgentConversationMessageRole =
  (typeof agentConversationMessageRoles)[number];

export const creatorToolSchema = z.object({
  uuid: z.string().min(1),
  imageUrl: z.url(),
  targetUrl: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1).optional(),
  order: z.number(),
});

export const creatorToolsPageConfigSchema = z.array(creatorToolSchema);

export type CreatorTool = z.infer<typeof creatorToolSchema>;
